/**
 * ProductPageLayout — the shared skeleton for all 4 Lumetier product pages
 * (Galleries, Website, Studio Manager, Store). Each product wrapper file is
 * a thin component that just passes props to this layout.
 *
 * Layout:
 *  - Charcoal canvas with subtle film grain.
 *  - Top: Lumetier nav (reused from Landing).
 *  - Left column (40%): chapter eyebrow + display headline + descriptor +
 *    feature pill row + 2 CTAs.
 *  - Right column (60%): 3-tile tilted preview stack.
 *  - Lower-right: rotated 'CHAPTER 0X / 04 PRODUCTS' caption.
 *  - Bottom: ticker, then a quiet sibling-product strip + footer.
 */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { LumetierNav } from "@/pages/lumetier/Landing";
import {
  ChapterEyebrow,
  DisplayHeadline,
  GoldRule,
  GoldFoilButton,
  HairlineButton,
  RotatedCaption,
  FilmGrain,
  Ticker,
  EditorialNumeral,
} from "@/components/editorial";
import FeaturePillRow from "@/components/editorial/FeaturePillRow";
import TiltedPreviewStack from "@/components/editorial/TiltedPreviewStack";
import useEditorialReveal from "@/hooks/useEditorialReveal";
import useMouseParallax from "@/hooks/useMouseParallax";

const ALL_PRODUCTS = [
  { id: "galleries", chapter: "01", label: "Galleries",       to: "/lumetier/galleries" },
  { id: "website",   chapter: "02", label: "Website",         to: "/lumetier/website" },
  { id: "manager",   chapter: "03", label: "Studio Manager",  to: "/lumetier/manager" },
  { id: "store",     chapter: "04", label: "Store",           to: "/lumetier/store" },
];

export default function ProductPageLayout({
  chapter,
  label,
  headline,        // node (uses <em> for gold-italic accent)
  descriptor,
  features = [],
  previewItems = [],
  primaryCta = { label: "", href: "#" },
  secondaryCta = { label: "", href: "#" },
}) {
  useEditorialReveal();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const parallax = useMouseParallax({ strength: 10, damping: 0.06 });

  const siblings = ALL_PRODUCTS.filter((p) => p.chapter !== chapter);

  return (
    <div className="min-h-screen bg-[color:var(--charcoal)] text-[color:var(--cream)] antialiased">
      <LumetierNav />

      {/* HERO SPLIT */}
      <section className="relative overflow-hidden">
        <FilmGrain opacity={0.05} tone="cool" />
        <div className="relative max-w-[1480px] mx-auto px-6 lg:px-10 pt-16 pb-28 lg:pt-24 lg:pb-40">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-10 lg:gap-14 items-start">
            {/* LEFT */}
            <div className="max-w-xl">
              <ChapterEyebrow chapter={chapter} label={`Product \u00B7 ${String(label).toUpperCase()}`} />
              <GoldRule width="2rem" className="mt-4 mb-8" />
              <DisplayHeadline size="hero" tone="charcoal" data-reveal="rise">
                {headline}
              </DisplayHeadline>
              <p className="ed-body-lg mt-7 text-[color:var(--cream)]/70 max-w-md leading-relaxed" data-reveal="rise">
                {descriptor}
              </p>
              <div className="mt-8" data-reveal="rise">
                <FeaturePillRow items={features} tone="dark" />
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-4" data-reveal="rise">
                <GoldFoilButton href={primaryCta.href}>{primaryCta.label}</GoldFoilButton>
                <HairlineButton href={secondaryCta.href} tone="dark">{secondaryCta.label}</HairlineButton>
              </div>
            </div>

            {/* RIGHT — tilted preview stack with subtle cursor parallax */}
            <div className="relative hidden lg:block min-h-[640px]">
              <div
                className="absolute -top-2 -left-4 right-0 bottom-0"
                style={{
                  transform: `translate3d(${parallax.x}px, ${parallax.y}px, 0)`,
                  willChange: "transform",
                }}
              >
                <TiltedPreviewStack items={previewItems} />
              </div>
            </div>

            {/* mobile: simplified stacked preview */}
            <div className="lg:hidden grid grid-cols-1 gap-4 mt-4">
              {previewItems.slice(0, 2).map((it, i) => (
                <div key={i} className="relative aspect-[4/3] overflow-hidden bg-[color:var(--charcoal-soft)] border border-[color:var(--gold-foil)]/20">
                  {it.render || (it.image && (
                    <img src={it.image} alt={it.alt || ""} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Rotated chapter marker, lower-right */}
          <div className="hidden lg:block absolute right-4 bottom-10">
            <RotatedCaption tone="gold" side="right">
              {`CHAPTER ${chapter} / 04 PRODUCTS`}
            </RotatedCaption>
          </div>

          {/* Giant outline numeral, faint, behind the headline column */}
          <div className="hidden lg:block pointer-events-none absolute left-2 bottom-4 opacity-50">
            <EditorialNumeral value={chapter} size="xl" style="outline" tone="gold" />
          </div>
        </div>
      </section>

      {/* TICKER */}
      <Ticker
        tone="dark"
        items={[
          `LUMETIER \u00B7 PRODUCT ${chapter}`,
          String(label).toUpperCase(),
          "VOL. 01 \u2014 SPRING 2026",
          "BUILT BY A WORKING PHOTOGRAPHER",
        ]}
      />

      {/* SIBLING PRODUCTS */}
      <section className="relative border-t border-[color:var(--gold-foil)]/15 py-24 lg:py-32">
        <FilmGrain opacity={0.04} tone="cool" />
        <div className="relative max-w-[1480px] mx-auto px-6 lg:px-10">
          <div className="mb-10">
            <ChapterEyebrow chapter="❧" label="The other three rooms" />
            <GoldRule width="3rem" className="mt-4" />
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {siblings.map((s) => (
              <Link
                key={s.id}
                to={s.to}
                className="editorial-card-hover group block p-7 border border-[color:var(--gold-foil)]/15 hover:border-[color:var(--gold-foil)]/45 transition-colors bg-[color:var(--charcoal-soft)]/40"
              >
                <div className="flex items-baseline gap-5">
                  <EditorialNumeral value={s.chapter} size="md" style="outline" tone="gold" />
                  <div>
                    <div className="ed-eyebrow text-[color:var(--gold-base)]">{`Product ${s.chapter}`}</div>
                    <div className="ed-subhead mt-2 group-hover:text-[color:var(--gold-foil)] transition-colors">{s.label}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[color:var(--gold-foil)]/15 py-10">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-[color:var(--cream)]/60">
          <span className="ed-eyebrow">© {new Date().getFullYear()} LUMETIER CREATIVES</span>
          <div className="flex items-center gap-6">
            <Link to="/marketing" className="ed-eyebrow hover:text-[color:var(--gold-foil)] transition-colors">Why Lumetier</Link>
            <Link to="/admin/login" className="ed-eyebrow hover:text-[color:var(--gold-foil)] transition-colors">Sign in</Link>
            <Link to="/onboard" className="ed-eyebrow hover:text-[color:var(--gold-foil)] transition-colors">Get an invite</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
